import { AlertTriangle, Clock } from "lucide-react";
import { getDaysUntilExpiry, safeAvailable } from "@/store/coupons";
import type { Coupon } from "@/store/types";

/**
 * Banner about the member's soonest-expiring coupon. Urgent (red) within 30
 * days, informational (blue) otherwise. Hidden when there's no active coupon or
 * it's already expired.
 */
export default function ExpiryWarning({ coupon }: { coupon: Coupon | null }) {
  if (!coupon || !coupon.ExpiryDate) return null;
  const days = getDaysUntilExpiry(coupon.ExpiryDate);
  if (days < 0) return null;

  const expiry = new Date(coupon.ExpiryDate).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const uses = safeAvailable(coupon);
  const urgent = days <= 30;
  const dayText = days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`;
  const useText = `${uses} use${uses !== 1 ? "s" : ""}`;

  return (
    <div
      className={
        urgent
          ? "agd:mt-4.5 agd:flex agd:items-center agd:gap-2.5 agd:rounded-md agd:border agd:border-[#feb2b2] agd:bg-[#fff5f5] agd:px-4 agd:py-2.5 agd:text-[0.85rem] agd:font-medium agd:text-[#c53030]"
          : "agd:mt-4.5 agd:flex agd:items-center agd:gap-2.5 agd:rounded-md agd:border agd:border-[#bde8f7] agd:bg-[#f0f9fe] agd:px-4 agd:py-2.5 agd:text-[0.85rem] agd:font-medium agd:text-[#2b6cb0]"
      }
    >
      <span className="agd:flex-shrink-0">
        {urgent ? <AlertTriangle size={18} /> : <Clock size={18} />}
      </span>
      <span>
        {urgent ? <strong>Expiring soon: </strong> : "Next expiry: "}
        Your current coupon ({useText} remaining) expires <strong>{dayText}</strong> on {expiry}.
        {urgent ? " Use your remaining credits before they expire." : ""}
      </span>
    </div>
  );
}
